import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useHotel } from "@/lib/hotel";

/**
 * Co-branding for guest-facing surfaces.
 *
 * A QR arrival already carries the property's branding in the browser, but a
 * guest who signed in with an email has none — so we resolve it from their
 * active stay on the server. Cached module-wide (one in-flight request shared
 * by every caller) because the nav, the home screen and the episode PDF all
 * ask for it on the same render.
 */
// Keyed by user so a sign-out (or a different guest on a shared iPad) can
// never inherit the previous stay's branding. Failures are NOT cached — the
// first call can land before the access token does.
let cacheKey = null;
let cache = null;
let inflight = null;

export function resetGuestBrandCache() {
  cacheKey = null;
  cache = null;
  inflight = null;
}

/**
 * The boot splash in public/index.html runs before any bundle, so it can only
 * greet the guest by name if we leave the details where plain JS can find them.
 */
function rememberForLaunch(data) {
  const b = data?.branding || {};
  const name = b.brand_name || data?.hotel_name;
  try {
    if (!name && !b.logo_url) localStorage.removeItem("aura-launch-brand");
    else localStorage.setItem("aura-launch-brand", JSON.stringify({
      name, logo_url: b.logo_url || null, accent: b.accent_color || null,
    }));
  } catch { /* private mode */ }
}

function fetchBrand(userId) {
  if (cache && cacheKey === userId) return Promise.resolve(cache);
  if (!inflight || cacheKey !== userId) {
    cacheKey = userId;
    inflight = api.get("/patient/branding")
      .then((r) => {
        cache = r.data || {};
        rememberForLaunch(cache);
        return cache;
      })
      .catch(() => ({}))
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function useGuestBrand() {
  const hotelCtx = useHotel();
  const { user } = useAuth() || {};
  const isGuest = user?.role === "patient";
  const [server, setServer] = useState(cacheKey === user?.id ? cache : null);

  useEffect(() => {
    if (!isGuest) return;
    let alive = true;
    fetchBrand(user.id).then((d) => { if (alive) setServer(d); });
    return () => { alive = false; };
  }, [isGuest, user?.id]);

  // QR context wins — it is the property the guest is physically standing in.
  const qrBranding = hotelCtx?.ctx?.branding || null;
  const qrHotel = hotelCtx?.ctx?.hotel || null;
  const branding = (qrBranding && Object.keys(qrBranding).length ? qrBranding : null)
    || server?.branding
    || null;

  const hotelName = qrHotel?.name || server?.hotel_name || null;

  return {
    branding,
    hotelName,
    hotelId: qrHotel?.id || server?.hotel_id || null,
    roomNumber: hotelCtx?.ctx?.room?.room_number || server?.room_number || null,
    logoUrl: branding?.logo_url || null,
    // The display name a property may prefer over its legal name.
    brandName: branding?.brand_name || hotelName || null,
    hasBrand: Boolean(hotelName || branding?.logo_url),
  };
}
